/* @flow */

import * as React from 'react';

import type { BasicFunction } from '../../helpers/jsHelpers/types';

import { Messenger, MessengerEvents } from '../../helpers/messenger';
import './ErrorBoundary.css';

type DefaultProps = {
  +errorCallback?: BasicFunction,
};

type ErrorBoundaryPropType = {
  ...DefaultProps,
  +children: React.Node,
  +componentName: string,
};

type ErrorBoundaryStateType = {
  +error: ?Error,
};

const InitialState = Object.freeze({
  error: null,
});

class ErrorBoundary extends React.PureComponent<ErrorBoundaryPropType, ErrorBoundaryStateType> {
  static getDerivedStateFromError(error: Error): any {
    return { error };
  }

  static defaultProps: DefaultProps = {
    errorCallback: undefined,
  };

  constructor(props: ErrorBoundaryPropType) {
    super(props);
    this.state = { ...InitialState };
  }

  componentDidCatch(error: Error) {
    const { componentName, errorCallback } = this.props;
    const { name: errorName } = error;

    Messenger.emit(
      MessengerEvents.NOTIFY_ERROR,
      <div>
        {errorName} in &quot;{componentName}&quot;
      </div>,
      undefined,
      `${errorName} in ${componentName}`,
    );

    if (errorCallback) {
      errorCallback();
    }
  }

  render(): React.Node {
    const { children, componentName } = this.props;
    const { error } = this.state;

    if (error) {
      const { message, name } = error;
      return (
        <div className='errorBoundary'>
          <div className='errorBoundaryRow'>
            <span className='errorBoundaryLabel'>Component:</span>
            <span className='errorBoundaryValue'>{componentName}</span>
          </div>
          <div className='errorBoundaryRow'>
            <span className='errorBoundaryLabel'>Error:</span>
            <span className='errorBoundaryValue'>{name}</span>
          </div>
          <div className='errorBoundaryRow'>
            <span className='errorBoundaryLabel'>Message:</span>
            <span className='errorBoundaryValue'>{message}</span>
          </div>
        </div>
      );
    }

    return children;
  }
}

export default ErrorBoundary;
